import React, { useState, useEffect } from 'react';
import LocationSelector from '../../LocationSelector';
import SuccessModal from '../SuccessModal';
import ProfileSettingsModal from '../ProfileSettingsModal';
import ConfirmationModal from '../ConfirmationModal';
import SelectAndSubmitModal from '../SelectAndSubmitModal';
import RenderProgressModal from '../RenderProgressModal';
import NotesAttachmentsModal from '../NotesAttachmentsModal';
import UserSubmissionsModal from '../UserSubmissionsModal';
import OnboardingWizard from '../../OnboardingWizard';
import PWAInstallPrompt from '../../PWAInstallPrompt';
import AutoCaptureCamera from '../../AutoCaptureCamera';
import GamificationDisplay from '../../GamificationDisplay';
import QuickAttributionForm from '../auth/QuickAttributionForm';
import GoogleAuthForm from '../auth/GoogleAuthForm';
import MicrosoftAuthForm from '../auth/MicrosoftAuthForm';
import EmailAuthForm from '../auth/EmailAuthForm';
import { APP_VERSION } from '../../utils/constants';
import { useAuth } from '../../hooks/useAuth';
import { useInspections } from '../../hooks/useInspections';
import { useSubmission } from '../../hooks/useSubmission';
import { useUI } from '../../hooks/useUI';
import { useGamification } from '../../hooks/useGamification';

/**
 * ModalManager - Centralized modal rendering
 * Extracted from App.js Phase 3D Kill #2
 * Handles all modal components and their visibility state
 */
const ModalManager = ({
  currentRoute,
  showOnboarding,
  completeOnboarding,
  setQuickShotOtherNotes,
  backendUrl,
  attributionMode,
  setAttributionMode,
  submittedBy,
  setSubmittedBy,
  navigate,
  consecutiveScans,
  setConsecutiveScans,
  showGamification,
  setIsFireExtinguisherCameraOpen,
  useAutoCapture,
  isFireExtinguisherCameraOpen,
  soundDebug,
  showLocationPopup: showLocationPopupProp,
  setShowLocationPopup: setShowLocationPopupProp,
  isCustomBusinessEntry: isCustomBusinessEntryProp,
  setIsCustomBusinessEntry: setIsCustomBusinessEntryProp,
  showSuggestions: showSuggestionsProp,
  setShowSuggestions: setShowSuggestionsProp,
  showConfirmationPopup: showConfirmationPopupProp,
  setShowConfirmationPopup: setShowConfirmationPopupProp,
  confirmationData: confirmationDataProp,
  setConfirmationData: setConfirmationDataProp,
  showSuccessPopup: showSuccessPopupProp,
  setShowSuccessPopup: setShowSuccessPopupProp,
  successPopupData: successPopupDataProp,
  setSuccessPopupData: setSuccessPopupDataProp,
  selectedImage: selectedImageProp,
  setSelectedImage: setSelectedImageProp,
  businessSuggestions: businessSuggestionsProp,
  isLoadingSuggestions: isLoadingSuggestionsProp,
  showConfirmationNotesPopup: showConfirmationNotesPopupProp,
  setShowConfirmationNotesPopup: setShowConfirmationNotesPopupProp,
  showRenderProgress: showRenderProgressProp,
  setShowRenderProgress: setShowRenderProgressProp,
  renderStep: renderStepProp,
  setRenderStep: setRenderStepProp
}) => {
  // Extract ALL modal-related state from hooks
  const {
    user,
    currentUser,
    setCurrentUser,
    submittedOnBehalfOf,
    setSubmittedOnBehalfOf,
    showSubmittedByModal,
    setShowSubmittedByModal,
    showProfileModal,
    setShowProfileModal,
    showAttributionModal,
    setShowAttributionModal,
    authModalMode,
    setAuthModalMode,
    userProfile,
    loadUserProfile,
    saveUserProfile,
    setSubmittedOnBehalfOfUser,
    getFirstNameFromEmail,
    handleQuickAttribution,
    skipAttribution,
    handleGoogleOAuth,
    handleMicrosoftOAuth,
    logoutUser
  } = useAuth();

  const {
    inspections,
    modalImage,
    setModalImage
  } = useInspections();

  // Use props passed from App.js to avoid duplicate state from useSubmission
  const showLocationPopup = showLocationPopupProp;
  const setShowLocationPopup = setShowLocationPopupProp;
  const isCustomBusinessEntry = isCustomBusinessEntryProp;
  const setIsCustomBusinessEntry = setIsCustomBusinessEntryProp;
  const showSuggestions = showSuggestionsProp;
  const setShowSuggestions = setShowSuggestionsProp;
  const showConfirmationPopup = showConfirmationPopupProp;
  const setShowConfirmationPopup = setShowConfirmationPopupProp;
  const confirmationData = confirmationDataProp;
  const setConfirmationData = setConfirmationDataProp;
  const showSuccessPopup = showSuccessPopupProp;
  const setShowSuccessPopup = setShowSuccessPopupProp;
  const successPopupData = successPopupDataProp;
  const setSuccessPopupData = setSuccessPopupDataProp;
  const selectedImage = selectedImageProp;
  const setSelectedImage = setSelectedImageProp;
  const businessSuggestions = businessSuggestionsProp;
  const isLoadingSuggestions = isLoadingSuggestionsProp;

  // Use props from App.js
  const showConfirmationNotesPopup = showConfirmationNotesPopupProp;
  const setShowConfirmationNotesPopup = setShowConfirmationNotesPopupProp;

  const [versionShownThisSession, setVersionShownThisSession] = useState(false);

  // 🔧 Real Progress Tracker from App.js props
  const showRenderProgress = showRenderProgressProp;
  const setShowRenderProgress = setShowRenderProgressProp;
  const renderStep = renderStepProp;
  const setRenderStep = setRenderStepProp;

  // 🔧 TASK 6 FIX: Mark version as shown when first success popup appears
  useEffect(() => {
    if (showSuccessPopup && !versionShownThisSession) {
      setVersionShownThisSession(true);
    }
  }, [showSuccessPopup, versionShownThisSession]);

  const {
    setBusinessSuggestions,
    setIsLoadingSuggestions,
    businessName,
    setBusinessName,
    capturedImages,
    setCapturedImages,
    locationSelectedFromPopup,
    setLocationSelectedFromPopup,
    location,
    setLocation,
    notes,
    setNotes,
    gpsData,
    gpsError,
    setGpsError,
    isCapturingGPS,
    captureGPS,
    isAnalyzing,
    setIsAnalyzing,
    inspectionResult,
    setInspectionResult,
    isSubmitting,
    setIsSubmitting,
    mainPageAttachments,
    setMainPageAttachments,
    additionalImages,
    setAdditionalImages,
    additionalNotes,
    setAdditionalNotes,
    quickShotOtherNotes,
    selectedLocations,
    setSelectedLocations,
    nearestOffice,
    setNearestOffice,
    loadingNearest,
    setLoadingNearest,
    showProcessingModal,
    setShowProcessingModal,
    analyzeProgress,
    setAnalyzeProgress,
    handleLocationSelected,
    handleQuickShotSubmit,
    fetchBusinessSuggestions
  } = useSubmission();

  const {
    toasts,
    removeToast,
    showAttachmentModal,
    setShowAttachmentModal,
    currentAttachment,
    setCurrentAttachment,
    showToast,
    playSound,
    submissionBanner,
    setSubmissionBanner
  } = useUI();

  const {
    showUserSubmissionsModal,
    setShowUserSubmissionsModal,
    selectedUserForSubmissions,
    setSelectedUserForSubmissions,
    getUserTotalPoints,
    refreshMyScore,
    getSubmittedByName
  } = useGamification();

  

  return (
    <>
      {/* Image Modal */}
      {modalImage && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setModalImage(null)}>
          <div className="relative max-w-5xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <img
              src={modalImage}
              alt="Full size inspection"
              className="max-w-full max-h-full object-contain rounded-lg cursor-pointer"
              onClick={(e) => { e.stopPropagation(); setModalImage(null); }}
            />
            <button
              onClick={() => setModalImage(null)}
              className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white rounded-full p-2 transition-colors duration-300"
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showSubmittedByModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-black/90 backdrop-blur-md rounded-xl p-6 border border-white/20 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">📧 PyeBarkerFS Email Required</h3>
            <p className="text-white/70 text-sm mb-4">
              Enter your PyeBarkerFS email address to track submissions for the pilot program.
            </p>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const email = formData.get('email');
              setSubmittedOnBehalfOfUser(email);

              // Also update local submittedBy
              const displayName = email.split('@')[0].replace('.', ' ').replace('_', ' ');
              const capitalizedName = displayName.split(' ').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ');
              setSubmittedBy(capitalizedName);
            }}>
              <input
                name="email"
                type="email"
                placeholder="firstname.lastname@pyebarkerfs.com"
                value={submittedOnBehalfOf}
                onChange={(e) => setSubmittedOnBehalfOf(e.target.value)}
                autoFocus
                className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              />
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 backdrop-blur-md border border-white/20"
                >
                  Set Email
                </button>
                <button
                  type="button"
                  onClick={() => setShowSubmittedByModal(false)}
                  className="flex-1 bg-white/10 text-white font-bold py-3 px-6 rounded-lg hover:bg-white/20 transition-all duration-300"
                >
                  Cancel
                </button>
              </div>
              <p className="text-white/50 text-xs mt-3">
                ✅ Valid format: firstname.lastname@pyebarkerfs.com<br />
                💾 Saved locally for future submissions
              </p>
            </form>
          </div>
        </div>
      )}


      {/* Toast Notifications - Centered with Fade */}
      {toasts.length > 0 && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 space-y-2">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`max-w-md px-6 py-4 rounded-xl border backdrop-blur-md shadow-2xl transform transition-all duration-300 animate-fadeIn ${toast.type === 'success'
                  ? 'bg-[var(--pbfs-green)]/20 border-[var(--pbfs-green)]/50 text-white'
                  : toast.type === 'error'
                    ? 'bg-red-500/20 border-red-500/50 text-red-400'
                    : 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                }`}
            >
              <div className="flex items-center justify-between space-x-4">
                <div className="flex-1 text-base font-medium text-center">
                  {toast.message}
                </div>
                <button
                  onClick={() => removeToast(toast.id)}
                  className="text-white/70 hover:text-white hover:bg-white/20 rounded-full w-6 h-6 flex items-center justify-center transition-all duration-200 flex-shrink-0"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Real Progress Tracker - Shows between capture and modal */}
      <RenderProgressModal
        isVisible={showRenderProgress}
        progress={renderStep}
        currentStep={renderStep}
        onForceClose={() => {
          console.warn('⚠️ Force closing stuck progress modal');
          setShowRenderProgress(false);
          setShowLocationPopup(true);
        }}
      />

      {/* Combined Location Selection & Confirmation Modal - NEW iOS-style */}
      <SelectAndSubmitModal
        isVisible={showLocationPopup || showConfirmationPopup}
        selectedImage={selectedImage}
        businessSuggestions={businessSuggestions}
        isLoadingSuggestions={isLoadingSuggestions}
        confirmationData={confirmationData}
        isSubmitting={isSubmitting}
        attachmentCount={mainPageAttachments.length}
        attachments={mainPageAttachments}
        setAttachments={setMainPageAttachments}
        userGPS={gpsData}
        onSubmit={async (selectedBusinessData) => {
          // Update business name and confirmation data
          setBusinessName(selectedBusinessData.name);
          setLocationSelectedFromPopup(true);
          setShowSuggestions(false);
          if (selectedBusinessData.isManual) {
            setIsCustomBusinessEntry(true);
          }

          // Update confirmation data
          setConfirmationData({
            image: selectedImage,
            address: selectedBusinessData.name,
            fullBusiness: selectedBusinessData.fullBusiness
          });

          // Close modal
          setShowLocationPopup(false);
          setShowConfirmationPopup(false);

          // Refresh score from server before showing popup
          await refreshMyScore();
          setShowSuccessPopup(true);
          setSuccessPopupData({
            points: getUserTotalPoints(),
            message: 'Tag captured successfully!',
            submittedBy: submittedBy,
            isProcessing: true  // Flag to show it's still processing
          });

          // Submit in background - 🔧 FIX: Pass businessName AND notes directly!
          // Normalize location details (nearby vs manual vs pin_map)
          const businessAddressOverride = selectedBusinessData.address || '';
          const businessPhoneOverride = selectedBusinessData.phone || '';
          const locationSourceOverride = selectedBusinessData.locationSource
            || (selectedBusinessData.fullBusiness?.isMapPin ? 'pin_map' : (selectedBusinessData.isManual ? 'manual' : 'nearby'));

          // If this came from pin-map, use the pin lat/lng as the effective gps_data for geocoding/branch
          let gpsDataOverride = null;
          const pinLat = selectedBusinessData.fullBusiness?.lat;
          const pinLng = selectedBusinessData.fullBusiness?.lng;
          if (selectedBusinessData.fullBusiness?.isMapPin && typeof pinLat === 'number' && typeof pinLng === 'number') {
            gpsDataOverride = {
              latitude: pinLat,
              longitude: pinLng,
              accuracy: null,
              timestamp: new Date().toISOString(),
              source: 'pin_map',
              business_phone: businessPhoneOverride || null
            };
          } else if (gpsData && gpsData.latitude && gpsData.longitude) {
            // 🔧 FIX: If not pin_map but we have device GPS, use that!
            // This ensures GPS is ALWAYS passed to submission, preventing null GPS data
            gpsDataOverride = { ...gpsData, source: 'device_gps' };
          }

          handleQuickShotSubmit(
            playSound,
            showToast,
            setSubmissionBanner,
            submittedBy,
            selectedImage,
            {
              setSuccessPopupData,
              setShowSuccessPopup
            },
            selectedBusinessData.name,  // 7th param: businessNameOverride
            selectedBusinessData.submissionNotes || '',  // 8th param: notesOverride
            businessAddressOverride,
            businessPhoneOverride,
            locationSourceOverride,
            gpsDataOverride
          );
        }}
        onEdit={() => {
          // Go back to location selection
          setShowConfirmationPopup(false);
          setShowLocationPopup(true);
        }}
        onAddNotes={(type, fileData) => {
          if (type === 'attachment' && fileData) {
            // Add attachment to state
            setMainPageAttachments(prev => [...prev, fileData]);
          } else if (type === 'camera') {
            // Camera handled in modal itself
            return;
          } else {
            // Just opening notes modal
            setShowConfirmationNotesPopup(true);
          }
        }}
        onClose={() => {
          setShowLocationPopup(false);
          setShowConfirmationPopup(false);
        }}
        showToast={showToast}
        playSound={playSound}
      />

      {/* OLD: Location Selection with Manual Entry - REPLACED BY SelectAndSubmitModal */}
      {false && (
        <LocationSelector
          isVisible={showLocationPopup}
          businessSuggestions={businessSuggestions}
          isLoadingSuggestions={isLoadingSuggestions}
          selectedImage={selectedImage}
          onSelectBusiness={(data) => {
            setBusinessName(data.name);
            setShowLocationPopup(false);
            setLocationSelectedFromPopup(true);
            setShowSuggestions(false);
            if (data.isManual) {
              setIsCustomBusinessEntry(true);
            }
            // Show confirmation popup
            setConfirmationData({
              image: selectedImage,
              address: data.address,
              fullBusiness: data.fullBusiness
            });
            setShowConfirmationPopup(true);
          }}
          onSkip={() => {
            // Skip location - go directly to confirmation with blank location
            setBusinessName(''); // Leave blank
            setShowLocationPopup(false);
            setLocationSelectedFromPopup(true);
            // Show confirmation popup with no location
            setConfirmationData({
              image: selectedImage,
              address: '', // Blank location
              fullBusiness: { name: '' }
            });
            setShowConfirmationPopup(true);
          }}
          onClose={() => setShowLocationPopup(false)}
          showToast={showToast}
        />
      )}

      {/* Success Popup with Additional Notes - Apple-Style Polish */}
      {showSuccessPopup && successPopupData && (() => {
        // 🚫 REJECTION DETECTION - Check if this is a rejected/invalid tag
        const isRejection = successPopupData.isRejection || 
                            successPopupData.is_valid_tag === false || 
                            successPopupData.inspection?.is_valid_tag === false;
        
        const rejectionReason = successPopupData.validation_reason || 
                                successPopupData.inspection?.validation_reason || 
                                successPopupData.rejection_reason || '';
        
        // Get user-friendly rejection message
        const getRejectionMessage = (reason) => {
          const reasonLower = (reason || '').toLowerCase();
          
          if (reasonLower.includes('blur') || reasonLower.includes('focus')) {
            return { title: "Image Too Blurry", tip: "Hold your phone steady and tap on the tag to focus before capturing." };
          }
          if (reasonLower.includes('dark') || reasonLower.includes('lighting') || reasonLower.includes('light')) {
            return { title: "Poor Lighting", tip: "Move to a brighter area or use your flash. Natural light works best." };
          }
          if (reasonLower.includes('not a tag') || reasonLower.includes('no tag') || reasonLower.includes('invalid') || reasonLower.includes('unrelated')) {
            return { title: "No Tag Detected", tip: "Make sure you're photographing a fire extinguisher inspection tag." };
          }
          if (reasonLower.includes('face') || reasonLower.includes('person') || reasonLower.includes('body')) {
            return { title: "Wrong Subject", tip: "Point your camera at a fire extinguisher inspection tag, not a person." };
          }
          if (reasonLower.includes('angle') || reasonLower.includes('perspective')) {
            return { title: "Bad Angle", tip: "Hold your phone parallel to the tag for a straight-on shot." };
          }
          if (reasonLower.includes('partial') || reasonLower.includes('cropped') || reasonLower.includes('cut off')) {
            return { title: "Tag Partially Visible", tip: "Step back slightly to capture the entire tag in frame." };
          }
          
          return { title: "Image Issue", tip: "Center the tag in frame, hold steady, and try again." };
        };
        
        const rejection = isRejection ? getRejectionMessage(rejectionReason) : null;
        const inspectionId = successPopupData.inspection?.inspection_id || successPopupData.inspection_id;
        
        return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 pb-24 animate-fadeIn">
          {/* pb-24 = 96px padding at bottom to clear bottom nav on small phones */}
          <div className="bg-[var(--pbfs-navy)] backdrop-blur-md rounded-2xl p-6 sm:p-8 border border-solid border-[var(--pbfs-gray)]/50 max-w-md w-full relative shadow-2xl animate-scaleIn max-h-[calc(100vh-140px)] overflow-y-auto">
            
            {/* ============= REJECTION STATE ============= */}
            {isRejection && !successPopupData.isProcessing ? (
              <>
                {/* Warning Icon */}
                <div className="flex justify-center mb-6">
                  <div className="w-24 h-24 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <svg className="w-16 h-16 text-yellow-500 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                
                {/* Rejection Header */}
                <h2 className="text-2xl font-bold text-white mb-2 text-center">
                  Oops! Try Again
                </h2>
                
                {/* Problem Title */}
                <h3 className="text-xl font-eurostile text-yellow-400 mb-4 text-center tracking-wide">
                  {rejection.title}
                </h3>
                
                {/* Explanation */}
                <div className="text-white/90 text-base mb-4 text-center space-y-3">
                  <p className="text-white/70 text-sm">
                    {rejectionReason || "We couldn't process this image."}
                  </p>
                  
                  {/* Helpful Tip Box */}
                  <div className="bg-blue-500/10 border border-blue-400/30 rounded-lg p-3">
                    <p className="text-blue-300 text-sm">
                      💡 {rejection.tip}
                    </p>
                  </div>
                  
                  {/* No Points Notice */}
                  <p className="text-white/50 text-xs">
                    No points were awarded for this scan.
                  </p>
                </div>
                
                {/* REJECTION Action Buttons */}
                <div className="flex flex-col space-y-3 mt-6">
                  {/* PRIMARY: Try Again */}
                  <button
                    onClick={() => {
                      setShowSuccessPopup(false);
                      setSuccessPopupData(null);
                      setSelectedImage(null);
                      setCapturedImages([]);
                      
                      setTimeout(() => {
                        setIsFireExtinguisherCameraOpen(true);
                        if (!gpsData && !isCapturingGPS) {
                          captureGPS();
                        }
                      }, 100);
                    }}
                    className="w-full bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-xl hover:shadow-2xl text-lg tracking-wide"
                  >
                    📸 TRY AGAIN
                  </button>
                  
                  {/* TODO: Add "Review Submission" button here when /admin/inspection/:id route is built */}
                  
                  {/* TERTIARY: Skip */}
                  <button
                    onClick={() => {
                      setShowSuccessPopup(false);
                      setSuccessPopupData(null);
                      setConsecutiveScans(0);
                      
                      setTimeout(() => {
                        setIsFireExtinguisherCameraOpen(true);
                      }, 300);
                    }}
                    className="w-full text-white/60 hover:text-white/80 font-medium py-2 transition-all duration-300 text-sm"
                  >
                    Skip for Now
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* ============= SUCCESS / PROCESSING STATE ============= */}
                {/* Professional Success Icon - Shows spinner while processing, checkmark when done */}
                <div className="flex justify-center mb-6">
                  {successPopupData.isProcessing ? (
                    <div className="w-24 h-24 rounded-full bg-yellow-500/20 flex items-center justify-center animate-pulse">
                      <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-[var(--pbfs-green)]/20 flex items-center justify-center">
                      <svg className="w-16 h-16 text-[var(--pbfs-green)] drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Personalized Greeting */}
                <h2 className="text-2xl font-bold text-white mb-2 text-center">
                  {successPopupData.isProcessing ? 'Hold tight...' : `Congratulations, ${currentUser}!`}
                </h2>

                {/* Main Title - Shows processing or captured */}
                <h3 className="text-3xl font-eurostile text-pb-green mb-4 text-center tracking-wide">
                  {successPopupData.isProcessing ? (
                    <span className="text-yellow-400">⏳ PROCESSING...</span>
                  ) : (
                    'TAG CAPTURED!'
                  )}
                </h3>

                {/* Body Text with Dynamic Encouraging Phrase */}
                <div className="text-white/90 text-base mb-6 text-center space-y-2">
                  {successPopupData.isProcessing ? (
                    <>
                      <p className="text-lg text-yellow-300">
                        🤖 AI is analyzing your tag...
                      </p>
                      <p className="text-white/60 text-sm">
                        This usually takes a few seconds
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-lg">
                        {(() => {
                          const phrases = [
                            { min: 1, max: 1, text: "Risk extinguished!", emoji: "", color: "" },
                            { min: 2, max: 2, text: "You're heating up!", emoji: "🔥", color: "text-pb-green" },
                            { min: 3, max: 3, text: "You're on fire!", emoji: "🔥", color: "text-pb-green font-bold" },
                            { min: 4, max: 4, text: "On a streak!", emoji: "🔥", color: "text-pb-green font-bold text-xl" },
                            { min: 5, max: 5, text: "Safety legend!", emoji: "⭐", color: "text-pb-green font-bold text-xl" },
                            { min: 6, max: 999, text: "UNSTOPPABLE!", emoji: "🚀", color: "text-pb-green font-bold text-2xl" }
                          ];

                          const phrase = phrases.find(p => consecutiveScans >= p.min && consecutiveScans <= p.max) || phrases[0];

                          return (
                            <span className={consecutiveScans > 1 ? `font-bold ${phrase.color}` : ""}>
                              {phrase.emoji && `${phrase.emoji} `}{phrase.text}
                            </span>
                          );
                        })()}
                      </p>
                      <p className="text-xl font-bold">
                        You've earned <span className="text-pb-green text-2xl font-eurostile">{getUserTotalPoints() || successPopupData.points || 0}</span> points so far!
                      </p>
                      <p className="text-white/60 text-sm">
                        Keep scanning to climb the leaderboard!
                      </p>
                    </>
                  )}
                </div>

            {/* BLOCKED OUT - Additional Notes Section - INSIDE SUCCESS STATE */}
            <div className="space-y-4" style={{ display: 'none' }}>
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  📝 Additional Notes (Optional)
                </label>
                <div className="relative">
                  <textarea
                    placeholder="Add any additional notes or observations..."
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  />
                  <div className="absolute bottom-3 right-3 flex space-x-2">
                    {/* File Attachment Button */}
                    <button
                      onClick={() => document.getElementById('additional-files-input').click()}
                      className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors duration-300 hidden"
                      title="Attach additional files (hidden for pilot)"
                    >
                      📎
                    </button>
                  </div>
                </div>
              </div>

              {/* Hidden file input for additional images */}
              <input
                id="additional-files-input"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  const newImages = [];

                  files.forEach(file => {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      newImages.push({
                        name: file.name,
                        data: event.target.result
                      });
                      if (newImages.length === files.length) {
                        setAdditionalImages(prev => [...prev, ...newImages]);
                      }
                    };
                    reader.readAsDataURL(file);
                  });
                }}
                className="hidden"
              />

              {/* Show uploaded additional images */}
              {additionalImages.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-white text-sm font-medium">
                    📸 Additional Images ({additionalImages.length})
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                    {additionalImages.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image.data}
                          alt={`Additional ${index + 1}`}
                          className="w-full h-16 object-cover rounded border border-white/20"
                        />
                        <button
                          onClick={() => setAdditionalImages(prev => prev.filter((_, i) => i !== index))}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons - Reordered & Polished */}
            <div className="flex flex-col space-y-3 mt-8">
              {/* PRIMARY: Scan Another - Bright Green CTA */}
              <button
                onClick={() => {
                  // Close popup
                  setShowSuccessPopup(false);
                  setSuccessPopupData(null);
                  setAdditionalNotes('');
                  setAdditionalImages([]);

                  // Reset form (but keep GPS data!)
                  setSelectedImage(null);
                  setBusinessName('');
                  setQuickShotOtherNotes('');
                  setMainPageAttachments([]);
                  // DON'T reset GPS: setGpsData(null);

                  // 🔄 CRITICAL: Clear captured images array for fresh start
                  setCapturedImages([]);

                  // ⏱️ BUGFIX: Add small delay before reopening camera to ensure state is cleared
                  setTimeout(() => {
                    setIsFireExtinguisherCameraOpen(true);

                    // GPS should already be captured - only request if needed (non-blocking)
                    if (!gpsData && !isCapturingGPS) {
                      console.log('🎯 Requesting GPS in background (non-blocking)');
                      captureGPS(); // Run in background, don't await
                    }
                  }, 100); // Small delay ensures state updates propagate
                }}
                className="w-full bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-xl hover:shadow-2xl text-lg tracking-wide"
              >
                SCAN ANOTHER
              </button>

              {/* SECONDARY: View Leaderboard */}
              <button
                onClick={() => {
                  setShowSuccessPopup(false);
                  setSuccessPopupData(null);
                  navigate('/leaderboard');
                }}
                className="w-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 hover:border-pb-green/60 text-white font-medium py-3.5 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99]"
              >
                View Leaderboard
              </button>

              {/* TERTIARY: Done - Subtle */}
              <button
                onClick={() => {
                  // 🔊 Play scan sound on Done button click
                  playSound('scan', 'done-button-click');

                  // Close the popup
                  setShowSuccessPopup(false);
                  setSuccessPopupData(null);
                  setAdditionalNotes('');
                  setAdditionalImages([]);
                  setConsecutiveScans(0); // Reset streak when done scanning 🔥

                  // Reset page to fresh state while preserving login AND GPS
                  setSelectedImage(null);
                  setLocation('');
                  setNotes('');
                  setSelectedLocations([]);
                  // KEEP GPS DATA: setGpsData(null);
                  setGpsError(null);

                  // 🔄 CAMERA AUTO-RESTART: Reopen camera immediately after Done
                  console.log('📷 Camera auto-restart: Reopening camera after successful scan...');
                  setTimeout(() => {
                    setIsFireExtinguisherCameraOpen(true);
                  }, 300); // Small delay to allow popup animation to complete
                  setIsAnalyzing(false);
                  setShowProcessingModal(false);
                  setAnalyzeProgress(0);
                  setInspectionResult(null);
                  setQuickShotOtherNotes('');
                  setMainPageAttachments([]);
                  setSubmissionBanner(null);
                  setNearestOffice(null);
                  setLoadingNearest(false);
                  setIsSubmitting(false);
                  setBusinessName('');
                  setBusinessSuggestions([]);
                  setIsLoadingSuggestions(false);
                  setShowSuggestions(false);
                  setIsCustomBusinessEntry(false);

                  // 🔄 CRITICAL: Clear captured images array for fresh start
                  setCapturedImages([]);

                  console.log('✅ Page reset to fresh state - Camera reopening for next capture');

                  // ⏱️ BUGFIX: Add small delay before reopening camera to ensure state is cleared
                  setTimeout(() => {
                    setIsFireExtinguisherCameraOpen(true);

                    // Auto-capture GPS for next scan
                    if (!gpsData && !isCapturingGPS) {
                      captureGPS();
                    }
                  }, 100); // Small delay ensures state updates propagate
                }}
                className="w-full bg-transparent hover:bg-white/5 text-white/60 hover:text-white/80 font-medium py-3 px-6 rounded-xl transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <span>Done</span>
              </button>
            </div>
              </>
            )}
          </div>
        </div>
        );
      })()}

      {/* Fire Extinguisher Camera Modal - Rendered at App level for proper z-index */}
      {isFireExtinguisherCameraOpen && useAutoCapture && !showOnboarding && (
        <AutoCaptureCamera
          onClose={() => setIsFireExtinguisherCameraOpen(false)}
          onCapture={async (imageData) => {
            // PILOT: Smart image handling - first image analyzed, additional as attachments
            if (capturedImages.length === 0) {
              // Start real progress tracking
              console.log('🎬 RENDER PROGRESS: Starting...');
              setShowRenderProgress(true);
              setRenderStep('capture');
              console.log('🎬 RENDER PROGRESS: Step 1 - Photo captured');

              // First image: set as main image for analysis
              setSelectedImage(imageData);
              const newImage = {
                id: Date.now(),
                data: imageData,
                timestamp: new Date().toISOString(),
                size: imageData.length,
                type: 'primary' // This will be analyzed
              };
              setCapturedImages([newImage]);
              console.log('📷 Primary image auto-captured with focus detection');

              // Close camera
              setIsFireExtinguisherCameraOpen(false);

              // Step 2: GPS
              setRenderStep('gps');
              console.log('🎬 RENDER PROGRESS: Step 2 - Getting location');
              const coords = await captureGPS();

              // Step 3: Fetch businesses
              setRenderStep('businesses');
              console.log('🎬 RENDER PROGRESS: Step 3 - Finding nearby businesses');
              if (coords) {
                // fetchBusinessSuggestions signature: (lat, lng, showToastFn)
                // Passing a number here breaks the flow (it gets called like a function).
                const businesses = await fetchBusinessSuggestions(coords.latitude, coords.longitude, showToast);
                console.log('✅ Found', businesses?.length || 0, 'nearby businesses');
              }

              // Step 4: Render
              setRenderStep('render');
              console.log('🎬 RENDER PROGRESS: Step 4 - Rendering');
              await new Promise(resolve => setTimeout(resolve, 500)); // Brief pause for smooth UX

              // Hide progress, show modal
              console.log('🎬 RENDER PROGRESS: Complete - Hiding progress, showing modal');
              setShowRenderProgress(false);
              setShowLocationPopup(true);
              console.log('✅ Location popup opened');
            } else {
              // Additional images: add as attachments (no analysis)
              const additionalImage = {
                id: Date.now(),
                data: imageData,
                timestamp: new Date().toISOString(),
                size: imageData.length,
                type: 'attachment' // Won't be analyzed, just stored
              };
              setCapturedImages(prev => [...prev, additionalImage]);
              console.log(`📷 Additional image ${capturedImages.length + 1} auto-captured`);
              showToast(`📸 Additional angle ${capturedImages.length} captured!`, 'success');
              setIsFireExtinguisherCameraOpen(false);
            }
          }}
        />
      )}

      {/* ### VOICE ASSISTANT - BLOCKED FOR MVP, CAN RE-ENABLE LATER ### */}
      {/* ### REMOVE COMMENT BELOW TO RESTORE VAPI.AI VOICE ASSISTANT ### */}
      {/*
      {vapiInstance && (
        <div className="fixed bottom-4 right-4 z-50" style={{ zIndex: 9999 }}>
          <div 
            className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-md rounded-full p-3 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
            onClick={isCallActive ? endVoiceCall : startVoiceCall}
            title={isCallActive ? "Click to end voice call" : "Click to start voice call"}
          >
            <div className="w-12 h-12 flex items-center justify-center text-white">
              {isCallActive ? (
                <span className="text-2xl animate-pulse">📞</span>
              ) : (
                <span className="text-2xl">🎤</span>
              )}
            </div>
          </div>
        </div>
      )}
      */}

      {/* BLOCKED OUT - Vapi Loading Indicator (Not ready for premiere) */}
      {/*
             {!vapiInstance && (
         <div className="fixed bottom-4 right-4 z-50 bg-black/80 backdrop-blur-md rounded-lg p-3 border border-white/20">
           <div className="flex items-center space-x-2">
             <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
             <span className="text-white text-xs">Loading voice assistant...</span>
           </div>
         </div>
       )}
       */}

      {/* Attribution Modal - Sign Up / Sign In */}
      {showAttributionModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-black/90 backdrop-blur-md rounded-xl p-6 border border-white/20 max-w-md w-full">
            {authModalMode === 'signup' ? (
              /* Sign Up Layout */
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-white">Create an account</h3>
                  <button
                    onClick={() => setShowAttributionModal(false)}
                    className="text-white/40 hover:text-white/60 text-xl transition-colors"
                  >
                    ×
                  </button>
                </div>

                <p className="text-white/70 text-sm mb-6">
                  Get credit for your submissions and enable auto-fill for future submissions.
                </p>

                {/* Show Microsoft Auth Form if microsoft mode is selected */}
                {attributionMode === 'microsoft' ? (
                  <MicrosoftAuthForm
                    onSuccess={(profile) => {
                      saveUserProfile({ ...profile, type: 'microsoft' });
                      setShowAttributionModal(false);
                    }}
                    onCancel={() => setAttributionMode('quick')}
                  />
                ) : (
                  <>
                    {/* Social Sign Up Options */}
                    <div className="space-y-3 mb-6">
                      <button
                        onClick={() => handleGoogleOAuth('signup')}
                        className="w-full flex items-center justify-center space-x-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 py-3 px-4 rounded-lg border border-red-500/30 transition-colors"
                      >
                        <span className="text-red-400">🔍</span>
                        <span>Sign up with Google</span>
                      </button>

                      <button
                        onClick={() => setAttributionMode('microsoft')}
                        className="w-full flex items-center justify-center space-x-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 py-3 px-4 rounded-lg border border-blue-500/30 transition-colors"
                      >
                        <span className="text-blue-400">💼</span>
                        <span>Sign up with Microsoft</span>
                      </button>
                    </div>

                    {/* Email Sign Up Form */}
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      const name = formData.get('name');
                      const email = formData.get('email');
                      const password = formData.get('password');

                      if (!name.trim()) {
                        showToast('⚠️ Please enter your name.', 'error');
                        return;
                      }
                      if (!email.trim()) {
                        showToast('⚠️ Please enter your email.', 'error');
                        return;
                      }
                      if (!password.trim()) {
                        showToast('⚠️ Please enter a password.', 'error');
                        return;
                      }

                      // Use existing handleQuickAttribution functionality
                      handleQuickAttribution(name, email);
                      showToast('✅ Account created successfully!', 'success');
                    }} className="space-y-4">
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">Name *</label>
                        <input
                          name="name"
                          type="text"
                          placeholder="Your full name"
                          required
                          className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>

                      <div>
                        <label className="block text-white text-sm font-medium mb-2">Email *</label>
                        <input
                          name="email"
                          type="email"
                          placeholder="your@email.com"
                          required
                          className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>

                      <div>
                        <label className="block text-white text-sm font-medium mb-2">Password *</label>
                        <input
                          name="password"
                          type="password"
                          placeholder="Create password (6+ chars)"
                          required
                          className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>

                      <button
                        type="submit"
                        className="w-full bg-green-500/20 hover:bg-green-500/30 text-green-400 font-bold py-3 px-4 rounded-lg border border-green-500/30 transition-colors"
                      >
                        ✅ Create Account
                      </button>
                    </form>

                    {/* Switch to Sign In */}
                    <div className="mt-6 pt-4 border-t border-white/20 text-center">
                      <p className="text-white/60 text-sm">
                        Already registered?
                        <button
                          onClick={() => setAuthModalMode('signin')}
                          className="text-blue-400 hover:text-blue-300 ml-1 underline transition-colors"
                        >
                          Sign in
                        </button>
                      </p>
                    </div>
                  </>
                )}
              </>
            ) : (
              /* Sign In Layout */
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-white">Welcome back</h3>
                  <button
                    onClick={() => setShowAttributionModal(false)}
                    className="text-white/40 hover:text-white/60 text-xl transition-colors"
                  >
                    ×
                  </button>
                </div>

                <p className="text-white/70 text-sm mb-6">
                  Get credit for your submissions and enable auto-fill for future submissions.
                </p>

                {/* Show Microsoft Auth Form if microsoft mode is selected */}
                {attributionMode === 'microsoft' ? (
                  <MicrosoftAuthForm
                    onSuccess={(profile) => {
                      saveUserProfile({ ...profile, type: 'microsoft' });
                      setShowAttributionModal(false);
                    }}
                    onCancel={() => setAttributionMode('quick')}
                  />
                ) : (
                  <>
                    {/* Social Sign In Options */}
                    <div className="space-y-3 mb-6">
                      <button
                        onClick={() => handleGoogleOAuth('signin')}
                        className="w-full flex items-center justify-center space-x-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 py-3 px-4 rounded-lg border border-red-500/30 transition-colors"
                      >
                        <span className="text-red-400">🔍</span>
                        <span>Sign in with Google</span>
                      </button>

                      <button
                        onClick={() => setAttributionMode('microsoft')}
                        className="w-full flex items-center justify-center space-x-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 py-3 px-4 rounded-lg border border-blue-500/30 transition-colors"
                      >
                        <span className="text-blue-400">💼</span>
                        <span>Sign in with Microsoft</span>
                      </button>
                    </div>

                    {/* Email Sign In Form */}
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      const email = formData.get('email');
                      const password = formData.get('password');

                      if (!email.trim()) {
                        showToast('⚠️ Please enter your email.', 'error');
                        return;
                      }
                      if (!password.trim()) {
                        showToast('⚠️ Please enter your password.', 'error');
                        return;
                      }

                      // Check if user exists (simple demo using profile system)
                      const existingProfile = loadUserProfile();
                      if (existingProfile && existingProfile.email === email.trim()) {
                        setCurrentUser(existingProfile.name);
                        setSubmittedBy(existingProfile.name);
                        localStorage.setItem('currentUser', existingProfile.name);
                        setShowAttributionModal(false);
                        showToast(`✅ Welcome back, ${existingProfile.name}!`, 'success');
                      } else {
                        showToast('❌ Invalid email or password. Please check your credentials or sign up.', 'error');
                      }
                    }} className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-white text-sm font-medium">Email</label>
                          <span className="text-white/40 text-xs">Last used</span>
                        </div>
                        <input
                          name="email"
                          type="email"
                          placeholder="Enter your email"
                          required
                          className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-white text-sm font-medium">Password</label>
                          <button
                            type="button"
                            onClick={() => showToast('🚧 Password reset coming soon!', 'info', 3000)}
                            className="text-white/40 hover:text-white/60 text-xs transition-colors"
                          >
                            Forgot your password?
                          </button>
                        </div>
                        <input
                          name="password"
                          type="password"
                          placeholder="Enter your password"
                          required
                          className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <button
                        type="submit"
                        className="w-full bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 font-bold py-3 px-4 rounded-lg border border-blue-500/30 transition-colors"
                      >
                        🔐 Sign in
                      </button>
                    </form>

                    {/* Switch to Sign Up */}
                    <div className="mt-6 pt-4 border-t border-white/20 text-center">
                      <p className="text-white/60 text-sm">
                        Don't have an account?
                        <button
                          onClick={() => setAuthModalMode('signup')}
                          className="text-green-400 hover:text-green-300 ml-1 underline transition-colors"
                        >
                          Sign up
                        </button>
                      </p>
                    </div>

                    {/* Quick Setup Option */}
                    <div className="mt-4 text-center">
                      <button
                        onClick={() => {
                          const name = prompt('Enter your name for quick setup:');
                          if (name && name.trim()) {
                            handleQuickAttribution(name.trim());
                          }
                        }}
                        className="text-white/40 hover:text-white/60 text-xs transition-colors"
                      >
                        Or use quick setup
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {/* BLOCKED OUT - Legacy attribution modes (replaced with new Sign Up/Sign In layout)
             {false && attributionMode === 'quick' && (
               <div className="space-y-4">
                 <p className="text-white/70 text-sm">
                   Get credit for your submissions and enable auto-fill for future submissions.
                 </p>
                 
                 <QuickAttributionForm 
                   onSubmit={handleQuickAttribution}
                   onCancel={skipAttribution}
                   allowSkip={attributionConfig.allowSkip}
                 />
                 
                 {(attributionConfig.providers.google || attributionConfig.providers.microsoft || attributionConfig.providers.email) && (
                   <div className="mt-6 pt-4 border-t border-white/20">
                     <p className="text-white/60 text-xs text-center mb-3">Or sign in with</p>
                     <div className="grid grid-cols-1 gap-2">
                       {(attributionConfig.providers.google || attributionConfig.providers.microsoft) && (
                         <div className="flex space-x-2">
                           {attributionConfig.providers.google && (
                             <button
                               onClick={() => setAttributionMode('google')}
                               className="flex-1 bg-red-500/20 text-red-400 border border-red-500/30 py-2 px-4 rounded-lg hover:bg-red-500/30 transition-colors text-sm"
                             >
                               🔍 Google
                             </button>
                           )}
                           {attributionConfig.providers.microsoft && (
                             <button
                               onClick={() => setAttributionMode('microsoft')}
                               className="flex-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 py-2 px-4 rounded-lg hover:bg-blue-500/30 transition-colors text-sm"
                             >
                               💼 Microsoft
                             </button>
                           )}
                         </div>
                       )}
                       {attributionConfig.providers.email && (
                         <button
                           onClick={() => setAttributionMode('email')}
                           className="w-full bg-green-500/20 text-green-400 border border-green-500/30 py-2 px-4 rounded-lg hover:bg-green-500/30 transition-colors text-sm"
                         >
                           📧 Email & Password
                         </button>
                       )}
                     </div>
                   </div>
                 )}
               </div>
             )}

             {attributionMode === 'google' && (
               <GoogleAuthForm 
                 onSuccess={(profile) => {
                   saveUserProfile({...profile, type: 'google'});
                   setShowAttributionModal(false);
                 }}
                 onCancel={() => setAttributionMode('quick')}
               />
             )}



             {attributionMode === 'email' && (
               <EmailAuthForm 
                 onSuccess={(profile) => {
                   saveUserProfile({...profile, type: 'email'});
                   setShowAttributionModal(false);
                 }}
                 onCancel={() => setAttributionMode('quick')}
               />
             )}
             */}
          </div>
        </div>
      )}

      {/* Profile Settings Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--pbfs-navy)] backdrop-blur-md rounded-xl border border-solid border-[var(--pbfs-gray)] max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white"><span className="text-[var(--pbfs-gray)]">👤</span> Profile Settings</h2>
                <button
                  onClick={() => setShowProfileModal(false)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Current Profile Info */}
              <div className="border-b border-white/20 pb-4 mb-6">
                <h3 className="text-lg font-semibold text-white mb-3">Current Profile</h3>
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-xl">
                      {currentUser ? currentUser.charAt(0).toUpperCase() : '?'}
                    </span>
                  </div>
                  <div>
                    <p className="text-white font-medium">{getFirstNameFromEmail(currentUser)}</p>
                    {userProfile?.email && (
                      <p className="text-white/50 text-xs">{userProfile.email}</p>
                    )}
                    <p className="text-white/60 text-sm">
                      {getUserTotalPoints()} point{getUserTotalPoints() !== 1 ? 's' : ''} earned
                    </p>
                  </div>
                </div>
              </div>

              {/* Profile Form */}
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const newName = formData.get('displayName');
                const newEmail = formData.get('email');

                if (newName && newName.trim()) {
                  // Update currentUser for points/leaderboard
                  setCurrentUser(newName.trim());
                  setSubmittedBy(newName.trim());

                  // Update userProfile with both name and email
                  const updatedProfile = {
                    ...userProfile,
                    name: newName.trim(),
                    displayName: newName.trim(),
                    email: newEmail && newEmail.trim() ? newEmail.trim() : userProfile?.email || ''
                  };

                  saveUserProfile(updatedProfile);
                  showToast('✅ Profile updated successfully!', 'success');
                  setShowProfileModal(false);
                } else {
                  showToast('⚠️ Display name is required', 'warning');
                }
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-white/70 text-sm font-medium mb-2">
                      Display Name *
                    </label>
                    <input
                      name="displayName"
                      type="text"
                      defaultValue={userProfile?.name || userProfile?.displayName || getFirstNameFromEmail(currentUser) || ''}
                      placeholder="Enter your display name"
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-lg border border-solid border-[var(--pbfs-gray)] text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-white/70 text-sm font-medium mb-2">
                      Email Address (PBFS)
                    </label>
                    <input
                      name="email"
                      type="email"
                      defaultValue={submittedOnBehalfOf || userProfile?.email || ''}
                      placeholder="Enter your PBFS email address"
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-lg border border-solid border-[var(--pbfs-gray)] text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]"
                    />
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      className="flex-1 bg-[var(--pbfs-green)] hover:bg-opacity-90 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300"
                    >
                      💾 Save Changes
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        logoutUser();
                        setShowProfileModal(false);
                      }}
                      className="px-6 py-3 bg-[var(--pbfs-gray)] hover:bg-opacity-90 text-white font-bold rounded-lg transition-all duration-300 border border-solid border-[var(--pbfs-gray)]"
                    >
                      🚪 Logout
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Attachment Viewer Modal */}
      {showAttachmentModal && currentAttachment && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-black/90 backdrop-blur-md rounded-xl border border-white/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">📎 {currentAttachment.name}</h2>
                <button
                  onClick={() => {
                    setShowAttachmentModal(false);
                    setCurrentAttachment(null);
                  }}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Attachment Content */}
              <div className="text-center">
                {currentAttachment.type?.startsWith('image/') ? (
                  <img
                    src={currentAttachment.url}
                    alt={currentAttachment.name}
                    className="max-w-full max-h-[70vh] mx-auto rounded-lg border border-white/20"
                  />
                ) : currentAttachment.type === 'application/pdf' ? (
                  <iframe
                    src={currentAttachment.url}
                    className="w-full h-[70vh] rounded-lg border border-white/20"
                    title={currentAttachment.name}
                  />
                ) : (
                  <div className="bg-white/10 p-8 rounded-lg border border-white/20">
                    <div className="text-white/60 mb-4">
                      📄 {currentAttachment.name}
                    </div>
                    <p className="text-white/50 text-sm mb-4">
                      This file type cannot be previewed in the browser.
                    </p>
                    <button
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = currentAttachment.url;
                        link.download = currentAttachment.name;
                        link.click();
                      }}
                      className="bg-blue-500/20 text-blue-400 px-4 py-2 rounded-lg hover:bg-blue-500/30 transition-colors"
                    >
                      💾 Download File
                    </button>
                  </div>
                )}
              </div>

              {/* Download Button for previewable files */}
              {(currentAttachment.type?.startsWith('image/') || currentAttachment.type === 'application/pdf') && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = currentAttachment.url;
                      link.download = currentAttachment.name;
                      link.click();
                    }}
                    className="bg-green-500/20 text-green-400 px-4 py-2 rounded-lg hover:bg-green-500/30 transition-colors"
                  >
                    💾 Download
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Wizard (First Launch Only) - Skip on all admin routes */}
      {showOnboarding && !currentRoute.startsWith('/admin') && (
        <OnboardingWizard
          onComplete={(email) => {
            // Set user identity from onboarding
            setSubmittedOnBehalfOfUser(email);

            // Also update local submittedBy state for Quick Shot form
            const displayName = email.split('@')[0].replace('.', ' ').replace('_', ' ');
            const capitalizedName = displayName.split(' ').map(word =>
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');
            setSubmittedBy(capitalizedName);

            completeOnboarding(email);
            showToast('🎉 Setup complete! Start scanning to earn points!', 'success', 5000);

            // Auto-open camera after onboarding
            setIsFireExtinguisherCameraOpen(true);

            // Auto-capture GPS in background
            if (!gpsData && !isCapturingGPS) {
              captureGPS();
            }
          }}
          playSound={playSound}
          backendUrl={backendUrl}
        />
      )}

      {/* User Submissions Modal */}
      <UserSubmissionsModal
        isVisible={showUserSubmissionsModal}
        userName={selectedUserForSubmissions}
        inspections={inspections}
        onClose={() => {
          setShowUserSubmissionsModal(false);
          setSelectedUserForSubmissions(null);
        }}
        getSubmittedByName={getSubmittedByName}
      />

      {/* Sound Debug Panel - Bottom Left (HIDDEN) */}
      <div className="fixed bottom-4 left-4 bg-red-900/90 backdrop-blur-sm px-3 py-2 rounded-lg border border-red-500/50 z-50 max-w-sm" style={{ display: 'none' }}>
        <div className="text-red-400 text-xs font-mono font-bold mb-1">
          🔊 Sound Debug:
        </div>
        <div className="text-white/80 text-xs space-y-0.5 max-h-32 overflow-y-auto">
          {soundDebug.length === 0 ? (
            <div className="text-white/40">No attempts yet...</div>
          ) : (
            soundDebug.slice(-5).map((log, i) => (
              <div key={i} className="font-mono">{log}</div>
            ))
          )}
        </div>
        {/* Test Button */}
        <button
          onClick={() => playSound('scan', 'manual-test-button')}
          className="mt-2 w-full bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-1.5 px-3 rounded transition-colors"
        >
          🔊 Test Sound
        </button>
      </div>

      {/* Version Indicator - Bottom Right - 🔧 TASK 6 FIX: Only show on first success popup */}
      {showSuccessPopup && !versionShownThisSession && (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
          {/* Version Badge */}
          <div
            className="bg-black/80 backdrop-blur-md px-3 py-2 rounded-lg border border-green-500/50"
            title="TagQuest version"
          >
            <div className="text-green-400 text-xs font-mono">
              🎯 {APP_VERSION} {backendUrl.includes('dev-api') ? '(DEV)' : '(STAGE)'}
            </div>
            <div className="text-white/60 text-xs">
              {new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' })}
            </div>
          </div>

          {/* Check for Updates Button */}
          <button
            onClick={() => {
              if (window.confirm('🔄 Force refresh to get the latest version?')) {
                // Clear service worker cache and reload
                if ('serviceWorker' in navigator) {
                  navigator.serviceWorker.getRegistrations().then(registrations => {
                    registrations.forEach(reg => reg.unregister());
                  });
                  caches.keys().then(names => {
                    names.forEach(name => caches.delete(name));
                  });
                }
                setTimeout(() => {
                  window.location.reload(true);
                }, 100);
              }
            }}
            className="bg-blue-600/90 hover:bg-blue-700 backdrop-blur-md px-3 py-2 rounded-lg border border-blue-500/50 transition-all text-white text-xs font-medium flex items-center justify-center gap-1"
            title="Force update to latest version"
          >
            <span>🔄</span>
            <span>Update</span>
          </button>
        </div>
      )}

      {/* Gamification Display */}
      {showGamification && submittedOnBehalfOf && (
        <GamificationDisplay
          userEmail={submittedOnBehalfOf}
          onPointsEarned={(phrase, points) => {
            showToast(`${phrase} +${points} points!`, 'success');
          }}
        />
      )}

      {/* PWA Install Prompt (Web Browser Only — hidden on native iOS/Android) */}
      {!showOnboarding && !window.Capacitor?.isNativePlatform?.() && (
        <PWAInstallPrompt
          onEmailSet={(email) => {
            setSubmittedOnBehalfOfUser(email);
          }}
        />
      )}

      {/* OLD: DEMO: Confirmation Popup - Review & Submit - REPLACED BY SelectAndSubmitModal */}
      {false && showConfirmationPopup && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--pbfs-navy)] backdrop-blur-xl rounded-2xl p-8 border border-solid border-[var(--pbfs-gray)] max-w-lg w-full mx-4 shadow-2xl">
            <h2 className="text-3xl font-eurostile text-pb-green mb-6 text-center flex items-center justify-center gap-2 tracking-wide">
              REVIEW & SUBMIT
            </h2>

            {/* Image Thumbnail */}
            {confirmationData.image && (
              <div className="mb-6 relative">
                <img
                  src={confirmationData.image}
                  alt="Captured"
                  className="w-full max-h-72 object-contain rounded-xl border-4 border-pb-green/20 shadow-xl"
                />
                <div className="absolute top-2 right-2 bg-green-600 text-white text-xs px-3 py-1 rounded-full font-bold">
                  ✓ Ready
                </div>
              </div>
            )}

            {/* Selected Address */}
            <div className="bg-black/50 rounded-xl p-5 mb-6 border border-pb-green/20">
              <div className="text-pb-gray text-sm mb-2 font-medium">Location</div>
              <div className="text-white font-bold text-xl">{confirmationData.address}</div>
              {confirmationData.fullBusiness?.address && (
                <div className="text-white/60 text-sm mt-2">{confirmationData.fullBusiness.address}</div>
              )}
            </div>

            {/* Attachment indicator (if files added) */}
            {mainPageAttachments.length > 0 && (
              <div className="text-white/50 text-xs mb-2 flex items-center gap-1">
                📎 {mainPageAttachments.length} attachment{mainPageAttachments.length > 1 ? 's' : ''}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 items-center">
              {/* Left: Paperclip + Edit Link */}
              <div className="flex gap-3 items-center">
                <button
                  onClick={() => {
                    setShowConfirmationNotesPopup(true);
                  }}
                  className="text-white/70 hover:text-white transition-all duration-200 text-xl"
                  disabled={isSubmitting}
                  title="Add notes & attachments"
                >
                  📎
                </button>
                <button
                  onClick={() => {
                    setShowConfirmationPopup(false);
                    setShowLocationPopup(true);
                  }}
                  className="text-[var(--pbfs-gray)] hover:text-white underline text-sm transition-all duration-200"
                  disabled={isSubmitting}
                >
                  Edit
                </button>
              </div>

              {/* Right: Wide Submit Button */}
              <button
                onClick={async () => {
                  if (isSubmitting) return;
                  // Play submission sound
                  playSound('camera-shutter', 'confirmation-submit');

                  setShowConfirmationPopup(false);

                  // Show success popup immediately (better UX)
                  setShowSuccessPopup(true);
                  setSuccessPopupData({
                    points: 1,
                    message: 'Tag captured successfully!',
                    submittedBy: submittedBy
                  });

                  // 🔧 FIX: Pass gpsData to prevent null GPS submissions
                  // Build gpsDataOverride from device GPS if available
                  const confirmGpsData = (gpsData && gpsData.latitude && gpsData.longitude)
                    ? { ...gpsData, source: 'device_gps' }
                    : null;

                  // Submit in background (don't await - user doesn't need to wait)
                  handleQuickShotSubmit(
                    playSound,
                    showToast,
                    setSubmissionBanner,
                    submittedBy,
                    confirmationData?.image,
                    {
                      setSuccessPopupData,
                      setShowSuccessPopup
                    },
                    confirmationData?.businessName || null,  // businessNameOverride
                    null,  // notesOverride
                    null,  // businessAddressOverride
                    null,  // businessPhoneOverride
                    null,  // locationSourceOverride
                    confirmGpsData  // 🔧 FIX: gpsDataOverride - ensures GPS is passed
                  );
                }}
                disabled={isSubmitting}
                className="flex-1 bg-[var(--pbfs-green)] hover:bg-opacity-90 disabled:bg-gray-600 text-white font-eurostile py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-2xl disabled:transform-none tracking-wide"
              >
                {isSubmitting ? '⏳ SUBMITTING...' : '✅ SUBMIT'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NOTES POPUP (from Confirmation Popup) */}
      {showConfirmationNotesPopup && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setShowConfirmationNotesPopup(false)}
        >
          <div
            className="bg-[var(--pbfs-navy)] rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-solid border-[var(--pbfs-gray)] relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Title */}
            <h2 className="text-white font-bold text-2xl mb-6 text-center">
              Add Notes & Attachments
            </h2>

            {/* Notes Text Area */}
            <textarea
              value={quickShotOtherNotes}
              onChange={(e) => setQuickShotOtherNotes(e.target.value)}
              onClick={(e) => {
                e.stopPropagation();
                e.target.focus();
              }}
              onFocus={(e) => {
                console.log('Notes textarea focused');
                e.target.style.pointerEvents = 'auto';
              }}
              placeholder="Add any additional notes here..."
              className="w-full h-32 bg-black/30 text-white rounded-lg p-4 mb-4 border border-solid border-[var(--pbfs-gray)] focus:outline-none focus:border-[var(--pbfs-green)] resize-none placeholder-white/40"
              style={{
                pointerEvents: 'auto',
                touchAction: 'auto',
                zIndex: 1,
                position: 'relative'
              }}
              autoFocus
            />

            {/* File Attachment Section */}
            <div className="mb-6">
              <label className="flex items-center justify-center gap-2 bg-black/30 hover:bg-black/40 text-white/70 hover:text-white border border-solid border-[var(--pbfs-gray)] hover:border-[var(--pbfs-green)] rounded-lg p-4 cursor-pointer transition-all duration-200">
                <span className="text-xl">📎</span>
                <span>Attach File</span>
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) {
                      setMainPageAttachments(prev => [...prev, ...files]);
                      playSound('camera-shutter', 'attachment-added');
                    }
                  }}
                />
              </label>
              {mainPageAttachments.length > 0 && (
                <div className="mt-3 text-white/60 text-sm">
                  {mainPageAttachments.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-black/20 rounded p-2 mb-1">
                      <span className="truncate flex-1">📎 {file.name}</span>
                      <button
                        onClick={() => {
                          setMainPageAttachments(prev => prev.filter((_, i) => i !== idx));
                        }}
                        className="text-red-400 hover:text-red-300 ml-2"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={() => setShowConfirmationNotesPopup(false)}
              className="w-full bg-[var(--pbfs-green)] hover:bg-opacity-90 text-white font-bold py-3 rounded-xl transition-all duration-300 transform hover:scale-105"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ModalManager;
